import { useApolloClient } from '@apollo/client';
import type { EmbeddingQuery, EmbeddingQueryVariables } from '@graphql/generated';
import { EmbeddingDocument } from '@graphql/generated';
import { createContext } from '@shared/utils/createContext';
import { useDebounce } from '@shared/utils/hooks/useDebounce';
import React, { useEffect, useMemo, useState } from 'react';
import { Embedding } from './useSearchFilteredGalleryItems';
import { useSearchTermTelemetry } from './useSearchTermTelemetry';

function useLatestEmbedding(term: string) {
  const apolloClient = useApolloClient();
  const [embedding, setEmbedding] = useState<number[]>();

  useEffect(() => {
    let didCancel = false;

    void (async () => {
      if (!term) {
        setEmbedding(undefined);
        return;
      }
      const result = await apolloClient.query<EmbeddingQuery, EmbeddingQueryVariables>({
        query: EmbeddingDocument,
        variables: { text: term },
        fetchPolicy: 'cache-first',
      });
      if (!didCancel) setEmbedding(result.data?.embedding);
    })();

    return () => {
      didCancel = true;
    };
  }, [apolloClient, term]);

  if (!term) return undefined;
  return embedding;
}

type SearchContextType = Readonly<{
  activeTerm: string;
  activeTermEmbedding: Embedding | undefined;
  setTerm: (term: string) => void;
}>;

const [SearchContextProvider, useSearchContext] = createContext<SearchContextType>({
  name: 'SearchContext',
});

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [term, setTerm] = useState<string>('');
  const debouncedTerm = useDebounce(term, 200);

  // For Chinese and similar, we relax the length requirement.
  // Single character like "红" would be a common search term.
  // Want to use modern /^\p{Script=Han}/u, but it requires es6 ts(1501)
  const isHanTerm = /^[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/.test(debouncedTerm); // Only need to check first char.
  const minLength = isHanTerm ? 1 : 3;
  const activeTerm = debouncedTerm.length >= minLength ? debouncedTerm : '';

  const activeTermEmbedding = useLatestEmbedding(activeTerm);
  useSearchTermTelemetry(activeTerm);

  const contextValue = useMemo(() => {
    return {
      activeTerm,
      activeTermEmbedding,
      setTerm,
    };
    // We skip `activeTerm` in deps because we want to wait for `activeTermEmbedding` before broadcasting changes.
    // This way `activeTerm` and `activeTermEmbedding` stay in sync. Otherwise `activeTerm` would change first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTermEmbedding, setTerm]);
  return <SearchContextProvider value={contextValue}>{children}</SearchContextProvider>;
};

export { useSearchContext };
